import type { CSSProperties, ReactNode } from "react";
import { useEffect, useState } from "react";
import { Chart as ChartJS, registerables } from "chart.js";
import { Bar, Line } from "react-chartjs-2";

import Skeleton from "../../components/states/Skeleton";
import { listCustomers, type CustomerRow } from "../../lib/admin/customers";
import {
  getUsageDetail,
  getUsageSummary,
  type UsageDetail,
  type UsageSummaryRow,
} from "../../lib/admin/usage";

// Chart.js v3+ requires explicit controller/element registration before
// any chart mounts (see components/Chart.tsx for the same guard). Safe
// to call more than once; chart.js dedupes by name internally.
ChartJS.register(...registerables);

/**
 * MER-211: /admin/usage. One row per customer: sessions (30d, with
 * trend), last login, active users (7d), a tiny sparkline. Clicking a
 * row expands the full drill-down (daily sessions, top tabs, top
 * widget drills, last login per user).
 */

// MER-161 brand palette (green-to-teal), matching components/Chart.tsx.
// Inline (not CSS vars) because Chart.js scriptable options run in
// canvas context where CSS custom properties aren't resolvable.
const BRAND_DEEP = "#0F6B5C";
const BRAND_TEAL = "#26A89F";
const TEXT_MUTED = "#95A5A6";

function relativeTime(iso: string | null): string {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "Never";
  const delta = Date.now() - t;
  const hour = 60 * 60 * 1000;
  const day = 24 * hour;
  if (delta < 60 * 1000) return "just now";
  if (delta < hour) return `${Math.max(1, Math.round(delta / (60 * 1000)))}m ago`;
  if (delta < day) return `${Math.round(delta / hour)}h ago`;
  return `${Math.round(delta / day)}d ago`;
}

const TREND_GLYPH: Record<UsageSummaryRow["sessionsTrend"], string> = {
  up: "↑",
  down: "↓",
  flat: "→",
};

const TREND_TONE: Record<UsageSummaryRow["sessionsTrend"], string> = {
  up: "var(--flag-green)",
  down: "var(--flag-red)",
  flat: "var(--text-muted)",
};

interface Row {
  customer: CustomerRow;
  summary: UsageSummaryRow;
}

interface PageState {
  rows: Row[];
  isLoading: boolean;
  error: string | null;
}

export default function UsageOverview() {
  const [state, setState] = useState<PageState>({ rows: [], isLoading: true, error: null });
  const [expandedTenantId, setExpandedTenantId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const customers = await listCustomers();
        const rows = await Promise.all(
          customers.map(async (customer) => ({
            customer,
            summary: await getUsageSummary(customer.tenantId),
          })),
        );
        if (cancelled) return;
        setState({ rows, isLoading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          isLoading: false,
          error: err instanceof Error ? err.message : String(err),
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main style={{ padding: "24px 32px", display: "grid", gap: 18 }}>
      <header style={headerStyle}>
        <div style={eyebrowStyle}>Admin</div>
        <h1 style={titleStyle}>Usage</h1>
      </header>

      {state.isLoading ? <Skeleton label="Loading usage" height={260} /> : null}
      {state.error ? <ErrorCard message={state.error} /> : null}
      {!state.isLoading && !state.error ? (
        <section style={panelStyle}>
          {state.rows.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
              No customers onboarded yet.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {state.rows.map((row) => (
                <CustomerUsageRow
                  key={row.customer.tenantId}
                  row={row}
                  expanded={expandedTenantId === row.customer.tenantId}
                  onToggle={() =>
                    setExpandedTenantId(
                      expandedTenantId === row.customer.tenantId ? null : row.customer.tenantId,
                    )
                  }
                />
              ))}
            </div>
          )}
        </section>
      ) : null}
    </main>
  );
}

function CustomerUsageRow(props: { row: Row; expanded: boolean; onToggle: () => void }) {
  const { row, expanded, onToggle } = props;
  const { customer, summary } = row;
  return (
    <div style={{ border: "1px solid var(--border-subtle)", borderRadius: 8, overflow: "hidden" }}>
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        style={{
          display: "grid",
          gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1.2fr",
          gap: 12,
          alignItems: "center",
          padding: "12px 16px",
          cursor: "pointer",
          background: "var(--bg-card)",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{customer.name}</div>
        <RowStat
          label="Sessions (30d)"
          value={
            <span>
              {summary.sessionsLast30d}{" "}
              <span style={{ color: TREND_TONE[summary.sessionsTrend], fontWeight: 700 }}>
                {TREND_GLYPH[summary.sessionsTrend]}
              </span>
            </span>
          }
        />
        <RowStat label="Last login" value={relativeTime(summary.lastLoginAt)} />
        <RowStat label="Active users (7d)" value={String(summary.activeUsersLast7d)} />
        <div style={{ height: 32 }}>
          {summary.sparkline.some((p) => p.count > 0) ? (
            <Sparkline points={summary.sparkline} />
          ) : (
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>No sign-ins yet</span>
          )}
        </div>
      </div>
      {expanded ? <UsageDetailPanel tenantId={customer.tenantId} /> : null}
    </div>
  );
}

function RowStat(props: { label: string; value: ReactNode }) {
  return (
    <div>
      <div style={metricLabelStyle}>{props.label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{props.value}</div>
    </div>
  );
}

function Sparkline(props: { points: Array<{ date: string; count: number }> }) {
  const { points } = props;
  return (
    <Line
      data={{
        labels: points.map((p) => p.date),
        datasets: [
          {
            data: points.map((p) => p.count),
            borderColor: BRAND_TEAL,
            backgroundColor: "rgba(38,168,159,0.12)",
            fill: true,
            tension: 0.35,
            pointRadius: 0,
            borderWidth: 1.5,
          },
        ],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false } },
        elements: { point: { radius: 0 } },
      }}
      height={32}
    />
  );
}

function UsageDetailPanel(props: { tenantId: string }) {
  const { tenantId } = props;
  const [detail, setDetail] = useState<UsageDetail | "loading" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    setDetail("loading");
    getUsageDetail(tenantId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch(() => {
        if (!cancelled) setDetail("error");
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (detail === "loading") {
    return (
      <div style={{ padding: 16 }}>
        <Skeleton label="Loading detail" height={120} />
      </div>
    );
  }
  if (detail === "error") {
    return (
      <div style={{ padding: 16, fontSize: 13, color: "var(--flag-red)" }}>
        Could not load usage detail for this customer.
      </div>
    );
  }

  const hasSessions = detail.dailySessions.some((p) => p.count > 0);

  return (
    <div
      style={{
        padding: 16,
        borderTop: "1px solid var(--border-subtle)",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 20,
        background: "rgba(0,0,0,0.015)",
      }}
    >
      <div style={{ gridColumn: "1 / -1" }}>
        <h3 style={detailTitleStyle}>Sessions per day (30 days)</h3>
        {hasSessions ? (
          <div style={{ height: 160 }}>
            <Line
              data={{
                labels: detail.dailySessions.map((p) => p.date.slice(5)),
                datasets: [
                  {
                    label: "Sessions",
                    data: detail.dailySessions.map((p) => p.count),
                    borderColor: BRAND_DEEP,
                    backgroundColor: "rgba(15,107,92,0.10)",
                    fill: true,
                    tension: 0.3,
                    pointRadius: 0,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  x: { ticks: { color: TEXT_MUTED, font: { size: 10 } }, grid: { display: false } },
                  y: {
                    beginAtZero: true,
                    ticks: { color: TEXT_MUTED, font: { size: 10 }, precision: 0 },
                  },
                },
              }}
              height={160}
            />
          </div>
        ) : (
          <EmptyNote text="No sign-ins yet" />
        )}
      </div>

      <div>
        <h3 style={detailTitleStyle}>Top tabs viewed</h3>
        {detail.topTabs.length === 0 ? (
          <EmptyNote text="No tab activity yet" />
        ) : (
          <div style={{ height: 200 }}>
            <Bar
              data={{
                labels: detail.topTabs.map((t) => t.label),
                datasets: [
                  {
                    data: detail.topTabs.map((t) => t.count),
                    backgroundColor: BRAND_TEAL,
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                indexAxis: "y",
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  x: {
                    beginAtZero: true,
                    ticks: { color: TEXT_MUTED, font: { size: 10 }, precision: 0 },
                  },
                  y: { ticks: { color: TEXT_MUTED, font: { size: 10 } } },
                },
              }}
              height={200}
            />
          </div>
        )}
      </div>

      <div>
        <h3 style={detailTitleStyle}>Top widget drills</h3>
        {detail.topWidgetDrills.length === 0 ? (
          <EmptyNote text="No widget drills yet" />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Widget</th>
                <th style={{ ...headerCellStyle, textAlign: "right" }}>Drills</th>
              </tr>
            </thead>
            <tbody>
              {detail.topWidgetDrills.map((w) => (
                <tr key={w.label}>
                  <td style={cellStyle}>{w.label}</td>
                  <td style={{ ...cellStyle, textAlign: "right" }}>{w.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ gridColumn: "1 / -1" }}>
        <h3 style={detailTitleStyle}>Last login per user</h3>
        {detail.lastLoginPerUser.length === 0 ? (
          <EmptyNote text="No sign-ins yet" />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={headerCellStyle}>User</th>
                <th style={headerCellStyle}>Last login</th>
              </tr>
            </thead>
            <tbody>
              {detail.lastLoginPerUser.map((u) => (
                <tr key={u.userId}>
                  <td style={cellStyle}>{u.email ?? u.userId.slice(0, 8)}</td>
                  <td style={cellStyle}>{relativeTime(u.lastLoginAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function EmptyNote(props: { text: string }) {
  return (
    <div style={{ fontSize: 13, color: "var(--text-muted)", padding: "8px 0" }}>{props.text}</div>
  );
}

function ErrorCard(props: { message: string }) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 12,
        padding: 20,
        color: "var(--flag-red)",
        fontSize: 13,
      }}
    >
      {props.message}
    </div>
  );
}

const headerStyle: CSSProperties = {
  padding: "22px 28px",
  background: "linear-gradient(110deg, var(--brand-mint) 0%, #E6F3EE 50%, #FFFFFF 100%)",
  border: "1px solid var(--brand-line)",
  borderRadius: 8,
};

const eyebrowStyle: CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 1,
  color: "var(--text-2)",
  fontWeight: 700,
};

const titleStyle: CSSProperties = {
  margin: "6px 0 0 0",
  fontSize: 26,
  fontWeight: 800,
  letterSpacing: -0.5,
  color: "var(--text)",
};

const panelStyle: CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 12,
  padding: 18,
  display: "grid",
  gap: 12,
};

const metricLabelStyle: CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  fontWeight: 700,
};

const detailTitleStyle: CSSProperties = {
  margin: "0 0 8px 0",
  fontSize: 12,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-2)",
};

const cellStyle: CSSProperties = {
  padding: "8px 10px",
  fontSize: 13,
  borderBottom: "1px solid var(--border-subtle)",
  color: "var(--text)",
};

const headerCellStyle: CSSProperties = {
  textAlign: "left",
  padding: "6px 10px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  borderBottom: "1px solid var(--border-subtle)",
  fontWeight: 700,
};
